import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { SxProps, Theme } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Autocomplete } from '_lib/components/Autocomplete/Autocomplete';
import { CircularProgress } from '_lib/components/CircularProgress/CircularProgress';
import { Divider } from '_lib/components/Divider/Divider';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { MaskedNumericInput } from '_lib/components/NumericTextField/NumericTextField';
import { TextField } from '_lib/components/TextField/TextField';
import { useDebounce } from '_lib/hooks/useDebounce';
import { toSxArray } from '_lib/utils/helpers';
import { reportException } from '_lib/utils/sentryService';
import { useOurSnackbar } from '_lib/utils/snackUtils';
import { FieldIssueProps } from '_lib/utils/validationErrorUtils';

interface SearchSelectProps<TListDTO> extends Partial<FieldIssueProps> {
  doSearch: (searchValue: string) => Promise<TListDTO[]>;
  selectedOption: TListDTO | null;
  trySetSelectedOption: (selectedOption: TListDTO | null) => Promise<boolean>;
  /* Used to determine the string value for a given option. It is used to fill the input upon selection */
  getSelectedOptionLabel: (option: string | TListDTO) => string;
  /* What is rendered in the dropdown */
  renderListOption: (option: TListDTO, liProps: React.HTMLAttributes<HTMLLIElement>) => React.ReactNode;
  label: string;
  placeHolder?: string;
  /** @default 300 */
  millisDelay?: number;
  required?: boolean;
  numericInputOnly?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export const SearchSelect = <TListDTO extends object>(props: SearchSelectProps<TListDTO>) => {
  const {
    doSearch,
    selectedOption,
    trySetSelectedOption,
    getSelectedOptionLabel,
    renderListOption,
    label,
    placeHolder,
    required,
    numericInputOnly,
    millisDelay,
    sx,
    issuePath,
    issues,
    disabled
  } = props;

  const { enqueueSnackbar } = useOurSnackbar();

  const [isLoading, setIsLoading] = useState(false);

  const [inputValue, setInputValue] = useState<string>('');

  const [options, setOptions] = useState<TListDTO[]>(selectedOption ? [selectedOption] : []);

  // Clear options when selected option is cleared from outside the component (ex: filter reset)
  useEffect(() => {
    if (!selectedOption) setOptions([]);
  }, [selectedOption]);

  const search = useCallback(
    async (searchValue: string) => {
      if (!searchValue) return [];
      setIsLoading(true);
      try {
        return await doSearch(searchValue);
      } catch (error) {
        enqueueSnackbar('Error Fetching Items');
        reportException(error);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [doSearch, enqueueSnackbar]
  );

  const handleManualSearch = async () => {
    const results = await search(inputValue);
    setOptions(results);
  };

  const debouncedSearchHandler = useDebounce(search, { millis: millisDelay });

  const searchHasCompleted = useRef(true);

  /* Need this because of a dumb timing issue when blurring after selecting an option */
  const optionHasBeenSelected = useRef(false);

  /**
   * TLDR; 'no results' popup only works when freeSolo is false.
   * For more info see: https://github.com/mui/material-ui/issues/18985#issuecomment-573347528
   *
   * When freeSolo is disabled (default), the autocomplete behaves like it expects there
   * to be an existing list of items to choose from, which gives us a nice customizable
   * unclickable list option if we have typed in something that does not match anything in the list.
   *
   * Since we are doing a search that does not get populated until we hit an external data source,
   * we want this to be enabled initially so that the user does not get the message saying there is no
   * data available when they click on the list.
   *
   * After we have fetched data, we set freeSolo: false, since we now have a data source in the list
   * that we can display. If we have no data, we will get a nice display saying 'no results'
   */
  const calculateFreeSolo = () => {
    if (isLoading) return true;
    if (!inputValue) return true;
    if (inputValue && !searchHasCompleted.current) return true;
    if (options.length) return true;
    return false;
  };

  return (
    <Autocomplete
      inputMode='search'
      filterOptions={(x) => x}
      disableClearable={true}
      freeSolo={calculateFreeSolo()}
      noOptionsText={'No results'}
      size='small'
      getOptionLabel={getSelectedOptionLabel}
      options={options}
      disabled={disabled}
      value={selectedOption || ''}
      inputValue={inputValue}
      readOnly={!!selectedOption}
      onChange={async (_e, value, reason) => {
        if (reason === 'selectOption' && value && typeof value !== 'string') {
          optionHasBeenSelected.current = true;
          const optionWasSet = await trySetSelectedOption(value);
          // If setter callback returns false (was not set), clear input
          if (!optionWasSet) {
            optionHasBeenSelected.current = false;
            setOptions([]);
            setInputValue('');
          }
        } else if (reason === 'clear') {
          optionHasBeenSelected.current = false;
          if (selectedOption) trySetSelectedOption(null);
        }
      }}
      onInputChange={async (_e, searchText, reason) => {
        // This reason is set when we select an option
        if (reason === 'reset') {
          setInputValue(searchText);
          return;
        }
        setInputValue(searchText);
        searchHasCompleted.current = false;
        const results = await debouncedSearchHandler(searchText);
        setOptions(results);
        searchHasCompleted.current = true;
      }}
      onBlur={() => {
        if (!selectedOption && !optionHasBeenSelected.current) {
          setOptions([]);
          setInputValue('');
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !event.repeat && !options.length && !isLoading) {
          event.stopPropagation();
          event.preventDefault();
          handleManualSearch();
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeHolder}
          error={
            !!issues && !!issuePath
              ? {
                  issues,
                  issuePath
                }
              : undefined
          }
          required={required}
          fullWidth
          slotProps={{
            input: {
              ...params.InputProps,
              inputComponent: numericInputOnly && !selectedOption ? MaskedNumericInput : undefined,
              endAdornment: (
                <React.Fragment>
                  {inputValue ? (
                    <IconButton
                      size='small'
                      onClick={() => {
                        if (selectedOption) trySetSelectedOption(null);
                        setOptions([]);
                        setInputValue('');
                      }}>
                      <CloseIcon />
                    </IconButton>
                  ) : null}
                  {inputValue && !selectedOption && (
                    <Divider
                      orientation='vertical'
                      flexItem
                      sx={{
                        zIndex: 1,
                        marginLeft: 1,
                        marginRight: 1,
                        borderWidth: 'thin'
                      }}
                    />
                  )}
                  {!selectedOption && (
                    <IconButton size='small' onClick={() => handleManualSearch()} disabled={isLoading || !inputValue}>
                      <SearchIcon sx={(theme) => ({ color: theme.palette.primary.main })} />
                      {isLoading && <CircularProgress sx={{ position: 'absolute', zIndex: 1 }} size={30} />}
                    </IconButton>
                  )}
                </React.Fragment>
              )
            }
          }}
        />
      )}
      renderOption={(props, option) => {
        return typeof option !== 'string' ? renderListOption(option, props) : <li {...props}>{option}</li>;
      }}
      sx={[
        ...toSxArray(sx),
        {
          '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            paddingRight: 1
          }
        }
      ]}
    />
  );
};
